import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useAccountStorage from "../../../../hooks/storage/useAccountStorage";
import { getUserLiveAccount } from "../../../../app/appSelectors";
import useIsOverlayComponentsOpened from "../../../../app/hooks/useIsOverlayComponentsOpened";
import useAnimationByStages from "../../../../hooks/useAnimationByStages";
import { LOCAL_STORAGE_KEYS } from "../../../../app/appConstants";

interface IUserStatusCircleProgressAnimationConfig {
    animationDurationMs: number,
    animationType: string,
    progressBarSizeMultiplier: number,
    showGlowingBackground: boolean,
}

const ANIMATION_STAGES_CONFIGS: IUserStatusCircleProgressAnimationConfig[] = [
    {
        animationDurationMs: 400,
        animationType: 'ease-in',
        showGlowingBackground: false,
        progressBarSizeMultiplier: 1,
    },
    {
        animationDurationMs: 400,
        animationType: 'ease-out',
        progressBarSizeMultiplier: 1.15,
        showGlowingBackground: true,
    },
    {
        animationDurationMs: 200,
        animationType: 'ease-in',
        progressBarSizeMultiplier: 1,
        showGlowingBackground: true,
    },
    {
        animationDurationMs: 400,
        animationType: 'ease-out',
        progressBarSizeMultiplier: 1.15,
        showGlowingBackground: true,
    },
    {
        animationDurationMs: 400,
        animationType: 'ease-in',
        showGlowingBackground: false,
        progressBarSizeMultiplier: 1,
    },
]

interface IUseUserStatusCircleProgressAnimationProps {
    isShowAnimation: boolean,
    currentProgress: number,
}

function useUserStatusCircleProgressAnimation(props: IUseUserStatusCircleProgressAnimationProps) {
    const { isShowAnimation, currentProgress } = props;
    const liveAccount = useSelector(getUserLiveAccount);
    const { getAccountStorageItem, setAccountStorageItem } = useAccountStorage(liveAccount?.name);
    const [progressToShow, setProgressToShow] =
        useState(getAccountStorageItem(LOCAL_STORAGE_KEYS.SHOWED_UPDATED_USER_STATUS_PROGRESS, currentProgress));

    const isOverlayComponentsOpened = useIsOverlayComponentsOpened();

    const isAllowedToShowAnimation = isShowAnimation && !isOverlayComponentsOpened;

    const onAnimationStartHandler = useCallback(() => {
        setProgressToShow(currentProgress);
        setAccountStorageItem(LOCAL_STORAGE_KEYS.SHOWED_UPDATED_USER_STATUS_PROGRESS, currentProgress);
    }, [currentProgress, setAccountStorageItem]);

    const {
        isAnimationRunning,
        debouncedStartAnimation,
        currentAnimationStageConfig,
        stopTimerToDebounceStartAnimation
    } =
        useAnimationByStages({
            animationStagesConfigs: ANIMATION_STAGES_CONFIGS,
            onAnimationStart: onAnimationStartHandler,
        });

    // Init animation
    useEffect(() => {
        if (!isAnimationRunning && isAllowedToShowAnimation && currentProgress !== progressToShow) {
            debouncedStartAnimation(500);
        }
        if (!isAnimationRunning && !isAllowedToShowAnimation) {
            stopTimerToDebounceStartAnimation();
        }
    }, [isAllowedToShowAnimation, isAnimationRunning, currentProgress, progressToShow, debouncedStartAnimation, stopTimerToDebounceStartAnimation]);

    return {
        currentAnimationStageConfig,
        isAnimationRunning,
        progressToShow: isShowAnimation ? progressToShow : currentProgress,
    }
}

export default useUserStatusCircleProgressAnimation;
